import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  CloudRain,
  Sun,
  CheckCircle,
  ArrowsClockwise,
  ArrowRight,
  MapPin,
  Wind,
  Compass,
  ListBullets,
} from "@phosphor-icons/react";
import { apiClient } from "@/lib/api";
import CityPulseView from "@/components/v2/CityPulseView";

const RADII = [
  { km: 5, label: "5 km", desc: "neighborhood" },
  { km: 10, label: "10 km", desc: "city" },
  { km: 15, label: "15 km", desc: "metro" },
];

// Display order for the by-direction list when no rain — keeps NSEW grouping calm
const COMPASS_ORDER = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

function rainTone(p) {
  if (p == null || p < 0.1) return { color: "#10b981", label: "Dry", soft: "rgba(16,185,129,0.15)" };
  // Trace: bumped contrast — was rgba(163,230,53,0.18) which faded into
  // cream paper. Now uses a deeper lime + 0.32 opacity so it actually
  // reads as "something here" rather than "almost dry".
  if (p < 0.5) return { color: "#65a30d", label: "Trace", soft: "rgba(101,163,13,0.32)" };
  if (p < 2) return { color: "#fbbf24", label: "Light", soft: "rgba(251,191,36,0.22)" };
  if (p < 5) return { color: "#f97316", label: "Moderate", soft: "rgba(249,115,22,0.22)" };
  if (p < 10) return { color: "#ef4444", label: "Heavy", soft: "rgba(239,68,68,0.22)" };
  return { color: "#a855f7", label: "Severe", soft: "rgba(168,85,247,0.24)" };
}

const COMPASS_PHRASE = {
  N: "to the north",
  NE: "to the north-east",
  E: "to the east",
  SE: "to the south-east",
  S: "to the south",
  SW: "to the south-west",
  W: "to the west",
  NW: "to the north-west",
};

// Bearing (degrees, 0 = N, clockwise) for each compass direction
const COMPASS_BEARING = {
  N: 0, NE: 45, E: 90, SE: 135, S: 180, SW: 225, W: 270, NW: 315,
};

/**
 * Predict whether rain is moving toward the user's centre based on wind FROM
 * direction + which compass direction has the strongest precipitation.
 *
 * Returns { compass, etaMin, intensityLabel, color } when a meaningful incoming
 * front is detected, else null.
 *
 * Logic:
 *   wind_direction_10m = direction the wind is blowing FROM (meteorological).
 *   So "wind from SW (225°)" implies weather systems are arriving from SW.
 *   We pick whichever rainy compass dot is within ±45° of the wind-FROM bearing
 *   and has the highest peak precip; ETA = radius_km / wind_speed_kmh × 60.
 */
function computeRainFront(ringPoints, wind, radiusKm) {
  if (!wind || wind.direction == null || wind.speed == null) return null;
  if (wind.speed < 3) return null; // calm winds → trajectory unreliable

  // Find rainy points (>= 0.5 mm/6h in the next 6 hours)
  const rainy = ringPoints.filter((p) => (p.max_precip_6h || 0) >= 0.5);
  if (!rainy.length) return null;

  // Filter to those within ±67.5° of where the wind is blowing FROM
  const windFromDeg = wind.direction;
  const aligned = rainy.filter((p) => {
    const b = COMPASS_BEARING[p.compass];
    if (b == null) return false;
    const diff = Math.abs(((b - windFromDeg + 540) % 360) - 180);
    return diff <= 67.5;
  });
  if (!aligned.length) return null;

  // Pick the wettest aligned direction
  const incoming = aligned.reduce((acc, p) =>
    (p.max_precip_6h || 0) > (acc.max_precip_6h || 0) ? p : acc,
  );

  // Travel time = distance / wind speed, capped at 6h
  const etaHours = radiusKm / wind.speed;
  const etaMin = Math.round(etaHours * 60);
  if (etaMin > 360) return null; // too slow to be meaningful

  return {
    compass: incoming.compass,
    etaMin,
    peak: incoming.max_precip_6h || 0,
  };
}

/**
 * NearbyRainPage — list-first layout (no compass radar). Sorts the 8 directional
 * places by rain intensity so the wettest direction surfaces first. Each card
 * shows the locality name, peak rain over 6h with a visual intensity bar, and
 * an ETA chip when rain is imminent. Tapping a card jumps the active location.
 */
export default function NearbyRainPage({
  location,
  onSelectLocation,
  forecast,
  confidence,
  theme,
  active,
}) {
  const [radius, setRadius] = useState(10);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // View mode toggle: "compass" (radial 8-direction) | "pulse" (city hubs list).
  // Persists across sessions via localStorage so commuters who always want the
  // pulse list don't have to flip it every visit.
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "compass";
    const saved = window.localStorage.getItem("nowcast.nearby-mode");
    return saved === "pulse" || saved === "compass" ? saved : "compass";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("nowcast.nearby-mode", mode);
    }
  }, [mode]);

  useEffect(() => {
    if (!active || !location?.latitude || !location?.longitude) return;
    if (mode !== "compass") return;
    if (data[radius]) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    apiClient
      .get("/nearby-nowcast", {
        params: {
          lat: location.latitude,
          lon: location.longitude,
          radius_km: radius,
        },
      })
      .then((res) => {
        if (cancelled) return;
        setData((prev) => ({ ...prev, [radius]: res.data }));
      })
      .catch((e) => !cancelled && setErr(e?.message || "Failed to load"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [active, location?.latitude, location?.longitude, radius, data]);

  // Reset cache when location changes
  useEffect(() => {
    setData({});
  }, [location?.latitude, location?.longitude]);

  const points = data[radius] || [];
  const ringPoints = useMemo(
    () => points.filter((p) => p.compass !== "Center"),
    [points],
  );

  // Sort wettest → driest, then by compass order as tiebreaker
  const sorted = useMemo(() => {
    const list = [...ringPoints];
    list.sort((a, b) => {
      const da = a.max_precip_6h || 0;
      const db = b.max_precip_6h || 0;
      if (db !== da) return db - da;
      return COMPASS_ORDER.indexOf(a.compass) - COMPASS_ORDER.indexOf(b.compass);
    });
    return list;
  }, [ringPoints]);

  const wettest = sorted[0];
  const peakMax = wettest?.max_precip_6h || 0;
  const anyRain = peakMax >= 0.5;
  // Differentiate genuine rain (≥0.5 mm) from "trace" (0.1–0.5 mm).
  // The old code lumped both together and produced "8 of 8 directions
  // show rain" when really only 2 had meaningful rain — misleading.
  const realRainHits = sorted.filter((p) => (p.max_precip_6h || 0) >= 0.5).length;
  const traceHits = sorted.filter((p) => {
    const v = p.max_precip_6h || 0;
    return v >= 0.1 && v < 0.5;
  }).length;

  // Rain-front trajectory hint — null if there's no incoming front.
  // Wind source priority (Phase 35 fix): the 700 hPa steering wind from
  // the rain-confidence engine is the meteorologically correct layer for
  // rain cell movement (~3 km altitude, free of surface friction). Surface
  // wind (10 m) is 2-5× slower and produced absurd ETAs (39 min when the
  // real 700 hPa steering said 8 min). We use the 700 hPa data when the
  // engine has classified storm motion, falling back to surface wind only
  // when the steering layer isn't available.
  const motion = confidence?.motion;
  const wind = motion && motion.speed_kmh != null
    ? {
        // Direction stored as "where wind/rain is coming FROM" — the
        // engine's `from_compass` already gives us this directly.
        direction: COMPASS_BEARING[motion.from_compass] ?? null,
        speed: motion.speed_kmh,
        layer: motion.steering_layer || "700hPa",
      }
    : forecast?.current
      ? {
          direction: forecast.current.wind_direction_10m,
          speed: forecast.current.wind_speed_10m,
          layer: "surface",
        }
      : null;
  const front = useMemo(
    () => (anyRain ? computeRainFront(ringPoints, wind, radius) : null),
    [anyRain, ringPoints, wind, radius],
  );

  // Headline now includes the actual place name so users know WHERE the
  // rain is, not just a vague compass direction. "Light rain over Tākarnām
  // (NW)" instead of the ambiguous "Light rain to the north-west".
  // When multiple directions tie at the wettest level (e.g., 4 directions
  // at exactly 0.8 mm), we surface the plural form so "Light rain to the
  // north" doesn't mislead users about the actual spread.
  const wettestPlaceName = wettest?.name || wettest?.label;
  const TIE_TOL = 0.05; // mm — anything within 0.05 mm of peak counts as tied
  const tiedDirs = anyRain
    ? sorted
        .filter((p) => Math.abs((p.max_precip_6h || 0) - peakMax) <= TIE_TOL)
        .map((p) => p.compass)
    : [];
  const headline = !ringPoints.length
    ? "Scanning your surroundings…"
    : anyRain
      ? tiedDirs.length >= 2
        ? `${rainTone(peakMax).label} rain across ${tiedDirs.join(", ")}`
        : wettestPlaceName
          ? `${rainTone(peakMax).label} rain over ${wettestPlaceName} (${wettest.compass})`
          : `${rainTone(peakMax).label} rain ${COMPASS_PHRASE[wettest.compass]}`
      : traceHits > 0
        ? `Trace amounts in ${traceHits} direction${traceHits === 1 ? "" : "s"}`
        : "Dry across all 8 directions";

  // Subtitle breaks out real rain vs trace so users can see the picture
  // accurately. Examples:
  //   "2 directions with rain · 6 with trace amounts"
  //   "1 direction with rain · others dry"
  //   "Trace amounts in 6 directions · 2 dry"
  const dryHits = 8 - realRainHits - traceHits;
  let sub;
  if (!ringPoints.length) {
    sub = `Looking at 8 places ${radius} km around you`;
  } else if (anyRain) {
    const parts = [
      `${realRainHits} ${realRainHits === 1 ? "direction" : "directions"} with rain`,
    ];
    if (traceHits > 0) parts.push(`${traceHits} with trace amounts`);
    if (dryHits > 0) parts.push(`${dryHits} dry`);
    sub = parts.join(" · ");
  } else if (traceHits > 0) {
    sub = `${dryHits} of 8 ${dryHits === 1 ? "is" : "are"} dry · only trace amounts elsewhere`;
  } else {
    sub = `No rain expected within ${radius} km of you`;
  }

  // Update the useEffect dependency array
  // (handled above)

  const darkMood = theme.id === "night" || theme.id === "storm";

  return (
    <div
      className="h-full w-full flex flex-col px-5 pt-2 pb-20"
      data-testid="page-nearby"
      style={{ color: theme.textPrimary }}
    >
      {/* Mode toggle — Compass (radial) vs Pulse (city hubs list). */}
      <div
        className="shrink-0 mb-2.5 flex p-0.5 rounded-full"
        style={{
          background: theme.chip,
          border: `1px solid ${theme.surfaceBorder}`,
        }}
        role="tablist"
        data-testid="nearby-mode-toggle"
      >
        <ToggleBtn
          active={mode === "compass"}
          onClick={() => setMode("compass")}
          theme={theme}
          darkMood={darkMood}
          icon={<Compass size={11} weight="duotone" />}
          label="Compass"
          testId="mode-compass"
        />
        <ToggleBtn
          active={mode === "pulse"}
          onClick={() => setMode("pulse")}
          theme={theme}
          darkMood={darkMood}
          icon={<ListBullets size={11} weight="bold" />}
          label="City Pulse"
          testId="mode-pulse"
        />
      </div>

      {mode === "pulse" ? (
        <CityPulseView location={location} theme={theme} active={active} />
      ) : (
        <CompassView
          location={location}
          onSelectLocation={onSelectLocation}
          theme={theme}
          loading={loading}
          err={err}
          radius={radius}
          setRadius={setRadius}
          sorted={sorted}
          ringPoints={ringPoints}
          headline={headline}
          sub={sub}
          front={front}
          peakMax={peakMax}
          darkMood={darkMood}
        />
      )}
    </div>
  );
}

// =========================================================================
// Compass view — original 8-direction radial dashboard, refactored into a
// child so the parent stays clean while we pivot between modes.
// =========================================================================
function CompassView({
  location,
  onSelectLocation,
  theme,
  loading,
  err,
  radius,
  setRadius,
  sorted,
  ringPoints,
  headline,
  sub,
  front,
  peakMax,
  darkMood,
}) {
  return (
    <>
      {/* Header — "Around you" implies current location; LocationBar lives on Page 1 only */}
      <div className="shrink-0 mb-3">
        <div
          className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-1 flex items-center gap-1.5"
          style={{ color: theme.textMuted }}
        >
          <MapPin size={10} weight="duotone" />
          <span>
            Around <span style={{ color: theme.textSecondary }}>{location?.name || "you"}</span> · 8 directions
          </span>
          {loading && (
            <ArrowsClockwise
              size={10}
              weight="bold"
              className="ml-auto animate-spin"
              style={{ color: theme.textMuted }}
            />
          )}
        </div>
        <h2
          className="font-display text-[20px] sm:text-[22px] font-semibold tracking-tight leading-snug"
          style={{ color: theme.textPrimary }}
          data-testid="nearby-headline"
        >
          {headline}
        </h2>
        <p
          className="text-[12px] mt-0.5"
          style={{ color: theme.textSecondary }}
          data-testid="nearby-sub"
        >
          {sub}
        </p>

        {/* Radius pills */}
        <div className="mt-3 flex gap-1.5" role="tablist" data-testid="radius-tabs">
          {RADII.map((r) => {
            const isActive = r.km === radius;
            return (
              <button
                key={r.km}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`radius-tab-${r.km}`}
                onClick={() => setRadius(r.km)}
                className="flex-1 py-1.5 rounded-full text-[11px] font-semibold transition-all"
                style={{
                  background: isActive ? theme.accent : theme.chip,
                  color: isActive ? (darkMood ? "#0f172a" : "#fff") : theme.textSecondary,
                  border: `1px solid ${isActive ? theme.accent : theme.surfaceBorder}`,
                  boxShadow: isActive ? `0 4px 14px ${theme.accent}40` : "none",
                }}
              >
                <span>{r.label}</span>{" "}
                <span className="text-[9.5px] opacity-75 font-medium">{r.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Cards list — fills remaining height, scrolls only inside */}
      <div
        className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1"
        style={{
          overscrollBehavior: "contain",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        data-testid="nearby-list"
      >
        {/* Rain-front trajectory banner — only shows when wind is carrying rain
            toward the user from one of the 8 surrounding directions. */}
        {front && (
          <RainFrontBanner front={front} theme={theme} radiusKm={radius} />
        )}

        <div className="space-y-1.5 pb-2">
          {sorted.length === 0 && !loading && (
            <div
              className="text-[12px] py-8 text-center rounded-2xl"
              style={{
                color: theme.textMuted,
                background: theme.surface,
                border: `1px solid ${theme.surfaceBorder}`,
              }}
            >
              No data available right now.
            </div>
          )}

          {sorted.length === 0 && loading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[52px] rounded-2xl animate-pulse"
                  style={{
                    background: theme.surface,
                    border: `1px solid ${theme.surfaceBorder}`,
                  }}
                />
              ))
            : sorted.map((p, idx) => (
                <NearbyCard
                  key={`${p.compass}-${p.lat}`}
                  point={p}
                  index={idx}
                  theme={theme}
                  radiusKm={radius}
                  peakMax={peakMax}
                  onTap={() =>
                    onSelectLocation({
                      ...location,
                      latitude: p.lat,
                      longitude: p.lon,
                      name: p.name || `${p.compass} ${radius}km`,
                      admin1: location?.admin1 || null,
                      country: location?.country || null,
                    })
                  }
                />
              ))}
        </div>
      </div>

      {err && (
        <div className="text-[11px] mt-1 shrink-0" style={{ color: "#f87171" }}>
          Couldn&apos;t load nearby rain ({err})
        </div>
      )}
      <style>{`[data-testid="nearby-list"]::-webkit-scrollbar { display: none; width: 0; height: 0; }`}</style>
    </>
  );
}

// =========================================================================
// (CompassRose removed in Phase 62.8 — the new RainFrontCompass on the
// rain-front banner is the single directional indicator now.)
// =========================================================================

function ToggleBtn({ active, onClick, theme, darkMood, icon, label, testId }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testId}
      onClick={onClick}
      className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-full text-[11px] font-bold transition-all"
      style={{
        background: active ? theme.accent : "transparent",
        color: active ? (darkMood ? "#0f172a" : "#fff") : theme.textSecondary,
        boxShadow: active ? `0 4px 14px ${theme.accent}40` : "none",
      }}
    >
      {icon}
      <span style={{ letterSpacing: "0.02em" }}>{label}</span>
    </button>
  );
}

/* ------------------------------------------------------------------
 * RainFrontCompass — circular SVG indicator that visualises where the
 * rain front is approaching FROM. Rotates an animated arrow into the
 * compass face from the source bearing and pulses a coloured dot at
 * that direction. "YOU" sits at the centre. Far more legible than the
 * plain text banner it replaces.
 * ------------------------------------------------------------------ */
function RainFrontCompass({ bearing, color, intensityPct }) {
  // Centre + radii for the SVG (drawn at 88×88, scales via viewBox)
  const SIZE = 88;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const R_OUTER = 38;
  const R_TICK = 34;
  const R_LABEL = 30;
  const R_DOT = 28;
  const R_ARROW_TAIL = 40;
  const R_ARROW_HEAD = 16;

  // Convert compass bearing (deg, 0=N, clockwise) to SVG angle.
  // SVG angle: 0° = +x axis (east), counter-clockwise positive.
  // We want bearing 0° (N) to point UP (-y) and 90° (E) to point RIGHT (+x).
  const toRad = (deg) => ((deg - 90) * Math.PI) / 180;

  // Cardinal label positions
  const cardinals = [
    { label: "N", deg: 0 },
    { label: "E", deg: 90 },
    { label: "S", deg: 180 },
    { label: "W", deg: 270 },
  ];

  // Source dot position (where rain is coming FROM)
  const sx = CX + R_DOT * Math.cos(toRad(bearing));
  const sy = CY + R_DOT * Math.sin(toRad(bearing));
  // Arrow goes FROM source TOWARD centre (rain is moving inwards)
  const tailX = CX + R_ARROW_TAIL * Math.cos(toRad(bearing));
  const tailY = CY + R_ARROW_TAIL * Math.sin(toRad(bearing));
  const headX = CX + R_ARROW_HEAD * Math.cos(toRad(bearing));
  const headY = CY + R_ARROW_HEAD * Math.sin(toRad(bearing));

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="block"
      style={{ width: 76, height: 76 }}
      data-testid="rain-front-compass"
    >
      {/* Outer ring */}
      <circle
        cx={CX}
        cy={CY}
        r={R_OUTER}
        fill="rgba(255,255,255,0.04)"
        stroke="rgba(15,23,42,0.18)"
        strokeWidth="1"
      />
      {/* 16 tick marks every 22.5° */}
      {Array.from({ length: 16 }).map((_, i) => {
        const a = toRad(i * 22.5);
        const x1 = CX + R_OUTER * Math.cos(a);
        const y1 = CY + R_OUTER * Math.sin(a);
        const x2 = CX + R_TICK * Math.cos(a);
        const y2 = CY + R_TICK * Math.sin(a);
        const major = i % 4 === 0;
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="rgba(15,23,42,0.32)"
            strokeWidth={major ? 1.2 : 0.6}
          />
        );
      })}
      {/* Cardinal labels */}
      {cardinals.map((c) => {
        const a = toRad(c.deg);
        const x = CX + R_LABEL * Math.cos(a);
        const y = CY + R_LABEL * Math.sin(a);
        return (
          <text
            key={c.label}
            x={x}
            y={y + 3}
            textAnchor="middle"
            fontSize="8"
            fontWeight="800"
            fill="rgba(15,23,42,0.55)"
          >
            {c.label}
          </text>
        );
      })}

      {/* Pulsing source dot — rain is at this bearing */}
      <motion.circle
        cx={sx}
        cy={sy}
        r={6}
        fill={color}
        animate={{ r: [4.5, 7.5, 4.5], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.circle
        cx={sx}
        cy={sy}
        r={3}
        fill="#fff"
        animate={{ r: [2.4, 3.6, 2.4] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Animated arrow: tail at the source direction, head pointing inward.
          The arrow draws itself in from a 0-length state on mount. */}
      <motion.line
        x1={tailX}
        y1={tailY}
        x2={headX}
        y2={headY}
        stroke={color}
        strokeWidth={2.2}
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
      />
      {/* Arrow head — small triangle pointing into the centre */}
      {(() => {
        // Direction vector pointing inward (toward centre)
        const dirX = CX - tailX;
        const dirY = CY - tailY;
        const len = Math.hypot(dirX, dirY) || 1;
        const ux = dirX / len;
        const uy = dirY / len;
        // Perpendicular for triangle base
        const px = -uy;
        const py = ux;
        const HEAD = 5;
        const BASE = 3.5;
        const tipX = headX + ux * HEAD;
        const tipY = headY + uy * HEAD;
        const baseLeftX = headX + px * BASE;
        const baseLeftY = headY + py * BASE;
        const baseRightX = headX - px * BASE;
        const baseRightY = headY - py * BASE;
        return (
          <motion.polygon
            points={`${tipX},${tipY} ${baseLeftX},${baseLeftY} ${baseRightX},${baseRightY}`}
            fill={color}
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.7 }}
            style={{ transformOrigin: `${headX}px ${headY}px` }}
          />
        );
      })()}

      {/* "You" pin at the centre */}
      <circle
        cx={CX}
        cy={CY}
        r={5.5}
        fill="rgba(255,255,255,0.9)"
        stroke={color}
        strokeWidth="1.5"
      />
      <circle cx={CX} cy={CY} r={2} fill={color} />

      {/* Intensity ring — partial arc from N going clockwise based on % */}
      {intensityPct > 0 && (
        <circle
          cx={CX}
          cy={CY}
          r={R_OUTER + 3}
          fill="none"
          stroke={color}
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeDasharray={`${(intensityPct / 100) * 2 * Math.PI * (R_OUTER + 3)} ${2 * Math.PI * (R_OUTER + 3)}`}
          transform={`rotate(-90 ${CX} ${CY})`}
          opacity="0.7"
        />
      )}
    </svg>
  );
}

function RainFrontBanner({ front, theme, radiusKm }) {
  const tone = rainTone(front.peak);
  const eta = front.etaMin;
  const phrase = COMPASS_PHRASE[front.compass];
  const sourceBearing = COMPASS_BEARING[front.compass] ?? 0;
  const intensityPct = Math.min(100, Math.round((front.peak / 8) * 100));

  // Format ETA as "in 25 min" / "in 1h 5m" / "in 2 hours"
  let etaLabel;
  if (eta < 60) etaLabel = `in ~${eta} min`;
  else if (eta < 90) etaLabel = `in ~1 h ${eta - 60} m`;
  else etaLabel = `in ~${Math.round(eta / 60)} h`;

  return (
    <motion.div
      className="relative rounded-2xl p-3 mb-2.5 overflow-hidden"
      style={{
        background: `linear-gradient(120deg, ${tone.soft} 0%, ${tone.soft}66 60%, transparent 100%)`,
        border: `1px solid ${tone.color}55`,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="rain-front-banner"
    >
      <div className="flex items-center gap-3 relative">
        {/* Real-time compass — replaces the old wind glyph */}
        <RainFrontCompass
          bearing={sourceBearing}
          color={tone.color}
          intensityPct={intensityPct}
        />

        <div className="flex-1 min-w-0">
          <div
            className="text-[9.5px] uppercase tracking-[0.18em] font-extrabold flex items-center gap-1"
            style={{ color: tone.color }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: tone.color }}
            />
            Rain front · live
          </div>
          <div
            className="text-[12.5px] font-semibold leading-snug mt-0.5"
            style={{ color: theme.textPrimary }}
            data-testid="rain-front-text"
          >
            {tone.label} rain approaching{" "}
            <span style={{ color: theme.textSecondary }}>
              from the {(phrase || "").replace("to the ", "")}
            </span>
          </div>
          <div
            className="flex items-baseline gap-2 mt-1"
            style={{ color: theme.textMuted }}
          >
            <span className="text-[10px] font-bold uppercase tracking-wider">
              Reaches you
            </span>
            <span
              className="text-[13px] font-extrabold tabular-nums leading-none"
              style={{ color: tone.color }}
              data-testid="rain-front-eta"
            >
              {etaLabel}
            </span>
            <span className="text-[10px] tabular-nums">· {radiusKm} km radius</span>
          </div>
        </div>
      </div>

      {/* Decorative animated trail */}
      <motion.div
        className="absolute bottom-0 left-0 h-0.5 rounded-full"
        style={{ background: tone.color }}
        initial={{ width: 0 }}
        animate={{ width: "100%" }}
        transition={{ duration: 1.6, ease: "easeOut" }}
      />
    </motion.div>
  );
}

function NearbyCard({ point, index, theme, radiusKm, peakMax, onTap }) {
  const peak = point.max_precip_6h || 0;
  const tone = rainTone(peak);
  const eta = point.eta_minutes;
  const temp = point.current_temp_c;
  const prob = point.max_prob_6h;
  // Bar fills relative to the wettest peak in this radius (so the user sees
  // the *relative* intensity ranking, not absolute mm).
  const barPct = peakMax > 0.05 ? Math.max(2, (peak / peakMax) * 100) : 0;
  const hasRain = peak >= 0.1;

  return (
    <motion.button
      type="button"
      onClick={onTap}
      data-testid={`nearby-card-${point.compass}`}
      className="relative w-full text-left rounded-2xl px-3 py-2 overflow-hidden transition-transform active:scale-[0.99]"
      style={{
        background: theme.surface,
        border: `1px solid ${hasRain ? `${tone.color}55` : theme.surfaceBorder}`,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: index * 0.035 }}
    >
      {/* Background tint when meaningful rain */}
      {hasRain && (
        <div
          className="absolute inset-0 pointer-events-none rounded-2xl"
          style={{
            background: `linear-gradient(90deg, ${tone.soft} 0%, transparent 70%)`,
          }}
          aria-hidden
        />
      )}

      <div className="relative flex items-center gap-2.5">
        {/* Direction badge */}
        <div
          className="shrink-0 flex items-center justify-center rounded-lg font-bold tabular-nums"
          style={{
            width: 32,
            height: 32,
            background: tone.soft,
            color: tone.color,
            border: `1px solid ${tone.color}40`,
            fontSize: 11,
            letterSpacing: "0.04em",
          }}
        >
          {point.compass}
        </div>

        {/* Body — single-line name + condition */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span
              className="text-[13px] font-semibold truncate"
              style={{ color: theme.textPrimary }}
            >
              {point.name || (
                <span style={{ color: theme.textPrimary }} className="font-semibold">
                  {/* Friendly fallback when the local cities DB doesn't
                      have a named settlement at this grid point. Was
                      "5 km north" / "5 km north-west" — reads like a
                      placeholder. Now: "Just north" / "North-west of you" */}
                  {COMPASS_PHRASE[point.compass]?.replace("to the ", "") || point.compass} of you
                </span>
              )}
            </span>
            {hasRain ? (
              <CloudRain size={11} weight="duotone" style={{ color: tone.color }} />
            ) : null}
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <span
              className="text-[10.5px] font-semibold"
              style={{ color: tone.color }}
            >
              {tone.label}
            </span>
            <span className="text-[10px]" style={{ color: theme.textMuted }}>
              · {peak.toFixed(1)} mm
              {prob != null && prob > 0 && ` · ${prob}%`}
            </span>
            {eta != null && hasRain && (
              <span
                className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-[1px] rounded-full text-[9.5px] font-bold tabular-nums"
                style={{
                  background: tone.soft,
                  color: tone.color,
                }}
                data-testid={`nearby-eta-${point.compass}`}
              >
                ~{eta}m
              </span>
            )}
          </div>
        </div>

        {/* Right column — temperature + intensity bar stacked compactly */}
        <div className="shrink-0 flex flex-col items-end gap-1 min-w-[40px]">
          {temp != null && (
            <span
              className="text-[14px] tabular-nums font-bold"
              style={{ color: theme.textPrimary }}
            >
              {Math.round(temp)}°
            </span>
          )}
          <div
            className="w-9 h-1 rounded-full overflow-hidden"
            style={{
              background: hasRain ? `${tone.color}25` : `${theme.surfaceBorder}`,
            }}
          >
            {hasRain && (
              <motion.div
                className="h-full rounded-full"
                style={{ background: tone.color }}
                initial={{ width: 0 }}
                animate={{ width: `${barPct}%` }}
                transition={{ duration: 0.55, ease: "easeOut" }}
              />
            )}
          </div>
        </div>
      </div>
    </motion.button>
  );
}
